import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminService } from '../../services/admin.service';
import type { AnimalListItem } from '../../services/animal.service';
import '../Animals.css';

function formatBirthDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU');
}

function formatSex(sex: number | null): string {
  if (sex === 1) return 'Самец';
  if (sex === 2) return 'Самка';
  return '—';
}

export default function AdminAnimals() {
  const navigate = useNavigate();
  const [animals, setAnimals] = useState<AnimalListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAnimals = () => {
    setIsLoading(true);
    setError(null);
    adminService
      .getAnimals()
      .then((response) => {
        if (!response.success || !response.data) {
          setError(response.message || 'Не удалось загрузить животных');
          return;
        }
        setAnimals(response.data);
      })
      .catch(() => {
        setError('Не удалось загрузить животных');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadAnimals();
  }, []);

  return (
    <div className="animals-page">
      <div className="animals-container">
        <div className="animals-header">
          <h1>Животные</h1>
          <Link to="/admin/animals/add" className="animals-create-button">
            + Добавить животное
          </Link>
        </div>

        {isLoading && (
          <div className="animals-content">
            <p>Загрузка...</p>
          </div>
        )}

        {!isLoading && error && (
          <div className="animals-content">
            <div className="animals-state">
              <p>{error}</p>
              <button type="button" className="animals-retry-button" onClick={loadAnimals}>
                Повторить
              </button>
            </div>
          </div>
        )}

        {!isLoading && !error && animals.length === 0 && (
          <div className="animals-content">
            <div className="animals-state">
              <p>Животные пока не добавлены</p>
            </div>
          </div>
        )}

        {!isLoading && !error && animals.length > 0 && (
          <div className="animals-table-wrapper">
            <table className="animals-table">
              <thead>
                <tr>
                  <th>№</th>
                  <th>Кличка</th>
                  <th>Тип</th>
                  <th>Порода</th>
                  <th>Пол</th>
                  <th>Категория</th>
                  <th>Дата рождения</th>
                  <th>Ферма</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {animals.map((animal) => (
                  <tr
                    key={animal.id}
                    className="animals-table-row"
                    onClick={() => navigate(`/admin/animals/${animal.id}`)}
                  >
                    <td>{animal.identifierNumber}</td>
                    <td>{animal.name || '—'}</td>
                    <td>{animal.animalType?.nameRu || '—'}</td>
                    <td>{animal.breed?.nameRu || '—'}</td>
                    <td>{formatSex(animal.sex)}</td>
                    <td>{animal.category?.nameRu || '—'}</td>
                    <td>{formatBirthDate(animal.birthDate)}</td>
                    <td>{animal.farm?.name || '—'}</td>
                    <td>
                      {animal.status ? (
                        <span className="animals-table-status">{animal.status.nameRu}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
